import React, { useEffect, useRef, useState } from 'react';
import { playIntroCountdownTick, playIntroGo } from '../audio/gameSfx.js';

/**
 * @param {{
 *   t: (key: string) => string,
 *   active: boolean,
 *   countdownFrom?: number,
 *   countdownIntervalMs?: number,
 *   goMs?: number,
 *   onComplete: () => void,
 * }} props
 */
export function GameStartBanner({
    t,
    active,
    countdownFrom = 10,
    countdownIntervalMs = 1000,
    goMs = 800,
    onComplete,
}) {
    const [phase, setPhase] = useState('countdown');
    const [countdown, setCountdown] = useState(countdownFrom);
    const [tickPulse, setTickPulse] = useState(false);
    const prevCountdownRef = useRef(countdownFrom);

    useEffect(() => {
        if (!active) return undefined;

        const from = Math.max(1, Math.floor(Number(countdownFrom) || 10));
        const intervalMs = Math.max(200, Math.floor(Number(countdownIntervalMs) || 1000));
        const goDurationMs = Math.max(300, Math.floor(Number(goMs) || 800));

        setPhase('countdown');
        setCountdown(from);
        prevCountdownRef.current = from;
        playIntroCountdownTick();

        const tickTimers = [];
        for (let step = 1; step < from; step += 1) {
            const value = from - step;
            tickTimers.push(
                window.setTimeout(() => {
                    setCountdown(value);
                    playIntroCountdownTick();
                }, step * intervalMs),
            );
        }

        const goTimer = window.setTimeout(() => {
            setPhase('go');
            playIntroGo();
        }, from * intervalMs);
        const doneTimer = window.setTimeout(
            () => {
                setPhase('done');
                onComplete();
            },
            from * intervalMs + goDurationMs,
        );

        return () => {
            tickTimers.forEach((id) => window.clearTimeout(id));
            window.clearTimeout(goTimer);
            window.clearTimeout(doneTimer);
        };
    }, [active, countdownFrom, countdownIntervalMs, goMs, onComplete]);

    useEffect(() => {
        if (phase !== 'countdown' || countdown === prevCountdownRef.current) return;
        prevCountdownRef.current = countdown;
        setTickPulse(true);
        const timerId = window.setTimeout(() => setTickPulse(false), 280);
        return () => window.clearTimeout(timerId);
    }, [countdown, phase]);

    if (!active || phase === 'done') return null;

    return (
        <div className="lp-game__start-banner" role="status" aria-live="polite">
            <p className="lp-game__start-banner-title">{t('game.intro.title')}</p>
            <p className="lp-game__start-banner-subtitle">{t('game.intro.subtitle')}</p>
            {phase === 'countdown' && (
                <p
                    className={`lp-game__start-banner-countdown${tickPulse ? ' lp-game__start-banner-countdown--pulse' : ''}`}
                    aria-label={String(countdown)}
                >
                    {countdown}
                </p>
            )}
            {phase === 'go' && (
                <p className="lp-game__start-banner-go" aria-label={t('game.intro.go')}>
                    {t('game.intro.go')}
                </p>
            )}
        </div>
    );
}
